import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg, API } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { TreeStructure, WindowsLogo, CloudArrowDown, DownloadSimple, ArrowsClockwise, Plus, Trash, Key, Copy, ShieldCheck } from '@phosphor-icons/react';

const ASSIGNABLE_ROLES = ['school_admin', 'principal', 'registrar', 'admissions_officer', 'teacher', 'finance_admin', 'student', 'guardian'];


const Badge = ({ children, tone = 'slate' }) => (
  <span className={`font-mono-label text-[9px] uppercase px-2 py-0.5 rounded bg-${tone}-100 text-${tone}-700`}>{children}</span>
);
const Card = ({ children, className = '', ...p }) => (
  <div className={`bg-white border border-slate-200 rounded-lg p-4 card-hover ${className}`} {...p}>{children}</div>
);
const Label = ({ children }) => <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">{children}</p>;

const statusTone = (s) => (s === 'online' || s === 'connected') ? 'green' : (s || '').startsWith('error') ? 'rose' : s === 'pending' ? 'amber' : 'slate';

function copy(text) { navigator.clipboard?.writeText(text); toast.success('Copied to clipboard'); }

// ---------------- Overview ----------------
const OverviewTab = ({ data }) => {
  if (!data) return <p className="text-sm text-slate-400">Loading...</p>;
  const s = data.summary;
  const kpis = [['AD agents', s.agents, 'blue'], ['Agents online', s.agents_online, 'green'], ['Cloud connectors', s.connections, 'blue'], ['Mappings configured', data.mappings_present ? 'Yes' : 'No', data.mappings_present ? 'green' : 'rose']];
  return (
    <div data-testid="dirsync-overview">
      {!data.mappings_present && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4 text-sm text-amber-800" data-testid="dirsync-no-mappings">
          No attribute mappings are configured yet. Directory sync uses <b>default deny</b> - until you add at least one mapping in
          <b> IAM &amp; IGA &rarr; Mappings</b>, every imported user is rejected. This is intentional (zero trust).
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {kpis.map(([label, n, tone]) => (
          <div key={label} className="bg-white border border-slate-200 rounded-md p-3" data-testid={`dirsync-kpi-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            <p className="font-mono-label text-[9px] uppercase text-slate-400">{label}</p>
            <p className={`font-display text-2xl font-extrabold text-${tone}-600`}>{n}</p>
          </div>
        ))}
      </div>
      <Card>
        <Label>Recent sync runs</Label>
        {(data.recent_runs || []).length === 0 && <p className="text-sm text-slate-400">No sync runs yet.</p>}
        {(data.recent_runs || []).map((r) => (
          <div key={r.id} className="py-2 border-t border-slate-100 text-sm flex items-center gap-3 flex-wrap" data-testid={`dirsync-run-${r.id}`}>
            <Badge tone="slate">{r.source}</Badge>
            <span className="text-xs text-slate-400">{(r.finished_at || '').slice(0, 16).replace('T', ' ')}</span>
            <span className="text-green-700">+{r.counts?.created || 0} created</span>
            <span className="text-blue-700">{r.counts?.updated || 0} updated</span>
            <span className="text-amber-700">{r.counts?.deactivated || 0} disabled</span>
            <span className="text-rose-700">{r.counts?.rejected || 0} rejected</span>
            <span className="text-slate-400">{r.counts?.skipped || 0} skipped</span>
          </div>
        ))}
      </Card>
    </div>
  );
};

// ---------------- AD Agents ----------------
const emptyDomain = () => ({ name: '', dc_hosts: '', base_dn: '', bind_dn: '', bind_password: '', use_ldaps: true });
const emptyForest = () => ({ name: '', trusts: '', domains: [emptyDomain()] });

const AgentDialog = ({ onClose }) => {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [interval, setInterval] = useState(60);
  const [forests, setForests] = useState([emptyForest()]);
  const [token, setToken] = useState(null);

  const upd = (fi, patch) => setForests((f) => f.map((x, i) => i === fi ? { ...x, ...patch } : x));
  const updDom = (fi, di, patch) => setForests((f) => f.map((x, i) => i === fi ? { ...x, domains: x.domains.map((d, j) => j === di ? { ...d, ...patch } : d) } : x));

  const mut = useMutation({
    mutationFn: () => api.post('/dirsync/agents', {
      name, sync_interval_minutes: Number(interval),
      forests: forests.map((f) => ({
        name: f.name, trusts: f.trusts.split(',').map((t) => t.trim()).filter(Boolean),
        domains: f.domains.map((d) => ({
          name: d.name, dc_hosts: d.dc_hosts.split(',').map((h) => h.trim()).filter(Boolean),
          base_dn: d.base_dn || null, bind_dn: d.bind_dn || null,
          bind_password: d.bind_password || null, use_ldaps: d.use_ldaps,
        })),
      })),
    }).then((r) => r.data),
    onSuccess: (d) => { setToken(d.enrollment_token); qc.invalidateQueries({ queryKey: ['dirsync-overview'] }); toast.success('Agent created'); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="agent-dialog">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5">
        {token ? (
          <div data-testid="agent-token-view">
            <h3 className="font-display text-lg font-bold mb-2">Agent created</h3>
            <p className="text-sm text-slate-500 mb-3">Download the agent, drop it on a Windows host that can reach your Domain Controllers, and run it. This enrollment token is shown once.</p>
            <div className="bg-slate-50 border border-slate-200 rounded p-2 flex items-center gap-2 mb-4">
              <code className="text-xs flex-1 break-all" data-testid="enrollment-token">{token}</code>
              <Button size="sm" variant="outline" onClick={() => copy(token)}><Copy /></Button>
            </div>
            <Button onClick={onClose} data-testid="agent-dialog-done">Done</Button>
          </div>
        ) : (
          <>
            <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><WindowsLogo /> New Windows AD agent</h3>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div><Label>Agent name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="HQ Domain Controller" data-testid="agent-name-input" /></div>
              <div><Label>Sync interval (minutes)</Label><Input type="number" value={interval} onChange={(e) => setInterval(e.target.value)} data-testid="agent-interval-input" /></div>
            </div>
            {forests.map((f, fi) => (
              <div key={fi} className="border border-slate-200 rounded-md p-3 mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <Badge tone="blue">Forest {fi + 1}</Badge>
                  {forests.length > 1 && <button className="text-rose-500 text-xs ml-auto" onClick={() => setForests((x) => x.filter((_, i) => i !== fi))}>remove</button>}
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div><Label>Forest root</Label><Input value={f.name} onChange={(e) => upd(fi, { name: e.target.value })} placeholder="example.com" data-testid={`forest-name-${fi}`} /></div>
                  <div><Label>Trusts (comma-sep)</Label><Input value={f.trusts} onChange={(e) => upd(fi, { trusts: e.target.value })} placeholder="partner.com, sub.example.com" /></div>
                </div>
                {f.domains.map((d, di) => (
                  <div key={di} className="bg-slate-50 rounded p-2 mb-2">
                    <div className="flex items-center gap-2 mb-1"><Badge>Domain {di + 1}</Badge>
                      {f.domains.length > 1 && <button className="text-rose-500 text-[10px] ml-auto" onClick={() => upd(fi, { domains: f.domains.filter((_, j) => j !== di) })}>remove</button>}</div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={d.name} onChange={(e) => updDom(fi, di, { name: e.target.value })} placeholder="corp.example.com" data-testid={`domain-name-${fi}-${di}`} />
                      <Input value={d.dc_hosts} onChange={(e) => updDom(fi, di, { dc_hosts: e.target.value })} placeholder="dc1.corp:636, dc2.corp" />
                      <Input value={d.base_dn} onChange={(e) => updDom(fi, di, { base_dn: e.target.value })} placeholder="DC=corp,DC=example,DC=com" />
                      <Input value={d.bind_dn} onChange={(e) => updDom(fi, di, { bind_dn: e.target.value })} placeholder="svc-onrid@corp.example.com" />
                      <Input type="password" value={d.bind_password} onChange={(e) => updDom(fi, di, { bind_password: e.target.value })} placeholder="bind password (optional here)" />
                      <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={d.use_ldaps} onChange={(e) => updDom(fi, di, { use_ldaps: e.target.checked })} /> Use LDAPS (636)</label>
                    </div>
                  </div>
                ))}
                <button className="text-blue-600 text-xs flex items-center gap-1" onClick={() => upd(fi, { domains: [...f.domains, emptyDomain()] })}><Plus /> add domain</button>
              </div>
            ))}
            <button className="text-blue-600 text-xs flex items-center gap-1 mb-4" onClick={() => setForests((x) => [...x, emptyForest()])}><Plus /> add forest</button>
            <div className="flex gap-2">
              <Button onClick={() => mut.mutate()} disabled={!name || mut.isPending} data-testid="agent-create-submit">{mut.isPending ? 'Creating...' : 'Create agent'}</Button>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const AgentsTab = ({ agents }) => {
  const qc = useQueryClient();
  const [showDialog, setShowDialog] = useState(false);
  const del = useMutation({ mutationFn: (id) => api.delete(`/dirsync/agents/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ['dirsync-overview'] }); toast.success('Agent removed'); }, onError: (e) => toast.error(errMsg(e)) });
  const regen = useMutation({ mutationFn: (id) => api.post(`/dirsync/agents/${id}/regenerate-enrollment`).then((r) => r.data), onSuccess: (d) => { copy(d.enrollment_token); qc.invalidateQueries({ queryKey: ['dirsync-overview'] }); toast.success('New enrollment token copied - re-download the agent'); }, onError: (e) => toast.error(errMsg(e)) });
  const download = (id) => { window.open(`${API}/dirsync/agents/${id}/download`, '_blank'); };

  return (
    <div data-testid="dirsync-agents">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">Downloadable on-prem agents. Each talks to multiple Domain Controllers across forests &amp; trusts over LDAP/LDAPS and pushes users here.</p>
        <Button onClick={() => setShowDialog(true)} data-testid="add-agent-btn"><Plus className="mr-1" /> New agent</Button>
      </div>
      {(agents || []).length === 0 && <Card><p className="text-sm text-slate-400">No agents yet. Create one, download the ZIP, and run it on a Windows host near your Domain Controllers.</p></Card>}
      <div className="space-y-3">
        {(agents || []).map((a) => (
          <Card key={a.id} data-testid={`agent-card-${a.id}`}>
            <div className="flex items-center gap-3 flex-wrap mb-2">
              <WindowsLogo className="text-blue-600" size={22} />
              <span className="font-semibold">{a.name}</span>
              <Badge tone={statusTone(a.status)}>{a.status}</Badge>
              {a.enrolled ? <Badge tone="green">enrolled</Badge> : <Badge tone="amber">not enrolled</Badge>}
              {a.hostname && <span className="text-xs text-slate-400">{a.hostname}</span>}
              {a.version && <span className="text-xs text-slate-400">v{a.version}</span>}
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={() => download(a.id)} data-testid={`agent-download-${a.id}`}><DownloadSimple className="mr-1" /> Download</Button>
                <Button size="sm" variant="outline" onClick={() => regen.mutate(a.id)} data-testid={`agent-regen-${a.id}`}><Key className="mr-1" /> Re-enroll</Button>
                <Button size="sm" variant="outline" onClick={() => del.mutate(a.id)}><Trash /></Button>
              </div>
            </div>
            <div className="text-xs text-slate-500 flex flex-wrap gap-4 mb-2">
              <span>Every {a.sync_interval_minutes} min</span>
              {a.last_sync_at && <span>Last sync {(a.last_sync_at).slice(0, 16).replace('T', ' ')}</span>}
              {a.last_result && <span className="text-green-700">+{a.last_result.created || 0} / {a.last_result.updated || 0} upd / {a.last_result.rejected || 0} rej</span>}
            </div>
            {(a.forests || []).map((f, i) => (
              <div key={i} className="border-t border-slate-100 pt-2 text-sm">
                <span className="font-medium">{f.name || 'forest'}</span>
                {f.trusts?.length > 0 && <span className="text-xs text-slate-400 ml-2">trusts: {f.trusts.join(', ')}</span>}
                <div className="pl-3 mt-1 space-y-0.5">
                  {(f.domains || []).map((d, j) => (
                    <div key={j} className="text-xs text-slate-500 flex items-center gap-2">
                      <Badge>{d.name}</Badge>
                      <span>{(d.dc_hosts || []).join(', ') || 'no DC hosts'}</span>
                      {d.use_ldaps && <Badge tone="green">LDAPS</Badge>}
                      {d.bind_password_set && <Badge tone="blue">bind pw set</Badge>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </Card>
        ))}
      </div>
      {showDialog && <AgentDialog onClose={() => setShowDialog(false)} />}
    </div>
  );
};

// ---------------- Cloud connectors (Entra + Okta) ----------------
const ConnectorForm = ({ kind }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState(kind === 'entra'
    ? { name: 'Entra ID', entra_tenant_id: '', client_id: '', client_secret: '' }
    : { name: 'Okta', org_url: '', api_token: '' });
  const create = useMutation({
    mutationFn: () => api.post('/dirsync/connections', { kind, ...form }).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dirsync-connections'] }); toast.success('Connector saved'); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <Card className="mb-4" data-testid={`${kind}-form`}>
      <Label>Add {kind === 'entra' ? 'Microsoft Entra ID' : 'Okta'} connector</Label>
      <div className="grid grid-cols-2 gap-2 mt-1">
        <Input placeholder="Display name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        {kind === 'entra' ? (
          <>
            <Input placeholder="Directory (tenant) ID" value={form.entra_tenant_id} onChange={(e) => setForm({ ...form, entra_tenant_id: e.target.value })} data-testid="entra-tenant-input" />
            <Input placeholder="Application (client) ID" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="entra-client-input" />
            <Input type="password" placeholder="Client secret" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} data-testid="entra-secret-input" />
          </>
        ) : (
          <>
            <Input placeholder="https://your-org.okta.com" value={form.org_url} onChange={(e) => setForm({ ...form, org_url: e.target.value })} data-testid="okta-org-input" />
            <Input type="password" placeholder="API token (SSWS)" value={form.api_token} onChange={(e) => setForm({ ...form, api_token: e.target.value })} data-testid="okta-token-input" />
          </>
        )}
      </div>
      <Button className="mt-3" size="sm" onClick={() => create.mutate()} disabled={create.isPending} data-testid={`${kind}-save-btn`}>Save connector</Button>
      <p className="text-xs text-slate-400 mt-2">
        {kind === 'entra'
          ? 'Needs an App Registration with Microsoft Graph application permissions User.Read.All + Group.Read.All (admin-consented).'
          : 'Create a read-only API token in Okta Admin -> Security -> API -> Tokens. Store it here; it is encrypted at rest.'}
      </p>
    </Card>
  );
};

const ConnectorsTab = ({ kind }) => {
  const qc = useQueryClient();
  const { data: conns } = useQuery({ queryKey: ['dirsync-connections'], queryFn: () => api.get('/dirsync/connections').then((r) => r.data) });
  const list = (conns || []).filter((c) => c.kind === kind);
  const test = useMutation({ mutationFn: (id) => api.post(`/dirsync/connections/${id}/test`), onSuccess: () => { qc.invalidateQueries({ queryKey: ['dirsync-connections'] }); toast.success('Connection OK'); }, onError: (e) => toast.error(errMsg(e)) });
  const sync = useMutation({ mutationFn: (id) => api.post(`/dirsync/connections/${id}/sync`).then((r) => r.data), onSuccess: (d) => { qc.invalidateQueries({ queryKey: ['dirsync-connections', 'dirsync-overview'] }); toast.success(`Synced: +${d.counts.created} created, ${d.counts.updated} updated, ${d.counts.rejected} rejected`); }, onError: (e) => toast.error(errMsg(e)) });
  const del = useMutation({ mutationFn: (id) => api.delete(`/dirsync/connections/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ['dirsync-connections'] }); toast.success('Connector removed'); }, onError: (e) => toast.error(errMsg(e)) });
  const toggleAuto = useMutation({ mutationFn: ({ id, auto_sync }) => api.patch(`/dirsync/connections/${id}`, { auto_sync }), onSuccess: () => { qc.invalidateQueries({ queryKey: ['dirsync-connections'] }); toast.success('Updated'); }, onError: (e) => toast.error(errMsg(e)) });

  return (
    <div data-testid={`dirsync-${kind}`}>
      <ConnectorForm kind={kind} />
      {list.length === 0 && <Card><p className="text-sm text-slate-400">No {kind === 'entra' ? 'Entra ID' : 'Okta'} connector yet.</p></Card>}
      <div className="space-y-3">
        {list.map((c) => (
          <Card key={c.id} data-testid={`connector-card-${c.id}`}>
            <div className="flex items-center gap-3 flex-wrap mb-2">
              <CloudArrowDown className="text-blue-600" size={20} />
              <span className="font-semibold">{c.name}</span>
              <Badge tone={statusTone(c.status)}>{c.status}</Badge>
              {c.credential_set ? <Badge tone="green">credential set</Badge> : <Badge tone="rose">no credential</Badge>}
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={() => test.mutate(c.id)} data-testid={`connector-test-${c.id}`}>Test</Button>
                <Button size="sm" onClick={() => sync.mutate(c.id)} disabled={sync.isPending} data-testid={`connector-sync-${c.id}`}><ArrowsClockwise className="mr-1" /> Sync now</Button>
                <Button size="sm" variant="outline" onClick={() => del.mutate(c.id)}><Trash /></Button>
              </div>
            </div>
            <div className="text-xs text-slate-500 flex flex-wrap gap-4 items-center">
              {c.entra_tenant_id && <span>tenant: {c.entra_tenant_id}</span>}
              {c.client_id && <span>client: {c.client_id}</span>}
              {c.org_url && <span>{c.org_url}</span>}
              {c.last_sync_at && <span>last sync {(c.last_sync_at).slice(0, 16).replace('T', ' ')}</span>}
              {c.last_result && <span className="text-green-700">+{c.last_result.created || 0} / {c.last_result.updated || 0} upd / {c.last_result.rejected || 0} rej</span>}
              <label className="flex items-center gap-1.5 ml-auto cursor-pointer" data-testid={`connector-autosync-${c.id}`}>
                <input type="checkbox" checked={c.auto_sync !== false} onChange={(e) => toggleAuto.mutate({ id: c.id, auto_sync: e.target.checked })} />
                <span>Nightly auto-sync</span>
              </label>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ---------------- Mappings (Group / attribute -> role) ----------------
const MappingsTab = () => {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: maps } = useQuery({ queryKey: ['dirsync-mappings'], queryFn: () => api.get('/iam/mappings').then((r) => r.data) });
  const [form, setForm] = useState({ claim: 'groups', op: 'contains', value: '', assign_role: 'teacher', priority: 10 });
  const add = useMutation({
    mutationFn: () => api.post('/iam/mappings', { tenant_id: user?.tenant_id, ...form, priority: Number(form.priority) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dirsync-mappings', 'dirsync-overview'] }); setForm({ ...form, value: '' }); toast.success('Mapping added'); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const del = useMutation({ mutationFn: (id) => api.delete(`/iam/mappings/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ['dirsync-mappings'] }); toast.success('Mapping removed'); }, onError: (e) => toast.error(errMsg(e)) });
  const sorted = [...(maps || [])].sort((a, b) => a.priority - b.priority);
  return (
    <div data-testid="dirsync-mappings">
      <Card className="mb-4">
        <Label>Add a mapping rule (evaluated top-down by priority; first match wins - default deny otherwise)</Label>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-1 items-end">
          <div>
            <p className="text-[10px] text-slate-400 mb-1">Attribute</p>
            <select className="w-full border border-slate-200 rounded-md h-9 px-2 text-sm" value={form.claim} onChange={(e) => setForm({ ...form, claim: e.target.value })} data-testid="mapping-claim">
              <option value="groups">Security group</option>
              <option value="department">Department</option>
              <option value="title">Job title</option>
              <option value="organization">Organization</option>
              <option value="userType">User type</option>
            </select>
          </div>
          <div>
            <p className="text-[10px] text-slate-400 mb-1">Match</p>
            <select className="w-full border border-slate-200 rounded-md h-9 px-2 text-sm" value={form.op} onChange={(e) => setForm({ ...form, op: e.target.value })} data-testid="mapping-op">
              <option value="contains">contains</option>
              <option value="equals">equals</option>
            </select>
          </div>
          <div>
            <p className="text-[10px] text-slate-400 mb-1">{form.claim === 'groups' ? 'Group name' : 'Value'}</p>
            <Input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder={form.claim === 'groups' ? 'Domain Teachers' : 'Science'} data-testid="mapping-value" />
          </div>
          <div>
            <p className="text-[10px] text-slate-400 mb-1">Assign role</p>
            <select className="w-full border border-slate-200 rounded-md h-9 px-2 text-sm" value={form.assign_role} onChange={(e) => setForm({ ...form, assign_role: e.target.value })} data-testid="mapping-role">
              {ASSIGNABLE_ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <Button onClick={() => add.mutate()} disabled={!form.value || add.isPending} data-testid="mapping-add-btn"><Plus className="mr-1" /> Add</Button>
        </div>
        <p className="text-xs text-slate-400 mt-2">Directory Sync is zero-trust: a directory user is only provisioned if a rule matches. Group rules use the security groups synced from AD (memberOf), Entra and Okta.</p>
      </Card>
      {sorted.length === 0 && <Card><p className="text-sm text-rose-600" data-testid="mappings-empty">No mappings yet - every imported user is rejected (default deny). Add at least one rule above.</p></Card>}
      <div className="space-y-2">
        {sorted.map((m) => (
          <Card key={m.id} data-testid={`mapping-${m.id}`}>
            <div className="flex items-center gap-3 flex-wrap text-sm">
              <Badge tone="slate">p{m.priority}</Badge>
              <span className="text-slate-500">if</span>
              <Badge tone="blue">{m.claim === 'groups' ? 'group' : m.claim}</Badge>
              <span className="text-slate-500">{m.op}</span>
              <span className="font-mono text-xs bg-slate-50 border border-slate-100 rounded px-1.5 py-0.5">{m.value}</span>
              <span className="text-slate-500">then role</span>
              <Badge tone="green">{m.assign_role.replace(/_/g, ' ')}</Badge>
              <Button size="sm" variant="outline" className="ml-auto" onClick={() => del.mutate(m.id)} data-testid={`mapping-del-${m.id}`}><Trash /></Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ---------------- Outbound provisioning ----------------
const OutboundTab = () => {
  const qc = useQueryClient();
  const { data: targets } = useQuery({ queryKey: ['dirsync-outbound'], queryFn: () => api.get('/dirsync/outbound/targets').then((r) => r.data) });
  const { data: events } = useQuery({ queryKey: ['dirsync-outbound-events'], queryFn: () => api.get('/dirsync/outbound/events').then((r) => r.data) });
  const [kind, setKind] = useState('entra');
  const [form, setForm] = useState({ name: '', entra_tenant_id: '', client_id: '', client_secret: '', sa_json: '', admin_subject_email: '', domain: '' });
  const create = useMutation({ mutationFn: () => api.post('/dirsync/outbound/targets', { kind, ...form }), onSuccess: () => { qc.invalidateQueries({ queryKey: ['dirsync-outbound'] }); setForm({ name: '', entra_tenant_id: '', client_id: '', client_secret: '', sa_json: '', admin_subject_email: '', domain: '' }); toast.success('Target saved'); }, onError: (e) => toast.error(errMsg(e)) });
  const test = useMutation({ mutationFn: (id) => api.post(`/dirsync/outbound/targets/${id}/test`), onSuccess: () => { qc.invalidateQueries({ queryKey: ['dirsync-outbound'] }); toast.success('Connection OK'); }, onError: (e) => toast.error(errMsg(e)) });
  const del = useMutation({ mutationFn: (id) => api.delete(`/dirsync/outbound/targets/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ['dirsync-outbound'] }); toast.success('Target removed'); }, onError: (e) => toast.error(errMsg(e)) });
  return (
    <div data-testid="dirsync-outbound">
      <Card className="mb-4">
        <Label>Add an outbound target (ONRID pushes create / disable OUT to this directory)</Label>
        <div className="flex gap-2 my-2">
          <button onClick={() => setKind('entra')} className={`text-sm px-3 py-1 rounded-md border ${kind === 'entra' ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-slate-200'}`} data-testid="outbound-kind-entra">Entra ID</button>
          <button onClick={() => setKind('google')} className={`text-sm px-3 py-1 rounded-md border ${kind === 'google' ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-slate-200'}`} data-testid="outbound-kind-google">Google Workspace</button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Display name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="outbound-name" />
          {kind === 'entra' ? (
            <>
              <Input placeholder="Directory (tenant) ID" value={form.entra_tenant_id} onChange={(e) => setForm({ ...form, entra_tenant_id: e.target.value })} />
              <Input placeholder="Application (client) ID" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} />
              <Input type="password" placeholder="Client secret" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} />
            </>
          ) : (
            <>
              <Input placeholder="Admin email to impersonate" value={form.admin_subject_email} onChange={(e) => setForm({ ...form, admin_subject_email: e.target.value })} />
              <Input placeholder="Primary domain (e.g. school.edu)" value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} />
              <textarea className="col-span-2 border border-slate-200 rounded-md p-2 text-xs font-mono h-24" placeholder="Paste service-account JSON key" value={form.sa_json} onChange={(e) => setForm({ ...form, sa_json: e.target.value })} data-testid="outbound-sajson" />
            </>
          )}
        </div>
        <Button className="mt-3" size="sm" onClick={() => create.mutate()} disabled={!form.name || create.isPending} data-testid="outbound-save-btn">Save target</Button>
        <p className="text-xs text-slate-400 mt-2">{kind === 'entra' ? 'Needs Graph application permission User.ReadWrite.All (admin-consented).' : 'Needs a service account with domain-wide delegation for scope admin.directory.user.'} When an ONRID account is created or disabled, it is pushed here automatically.</p>
      </Card>
      {(targets || []).length === 0 && <Card><p className="text-sm text-slate-400">No outbound targets yet.</p></Card>}
      <div className="space-y-3">
        {(targets || []).map((t) => (
          <Card key={t.id} data-testid={`outbound-card-${t.id}`}>
            <div className="flex items-center gap-3 flex-wrap mb-1">
              <ArrowsClockwise className="text-blue-600" size={18} />
              <span className="font-semibold">{t.name}</span>
              <Badge tone="blue">{t.kind === 'entra' ? 'Entra ID' : 'Google'}</Badge>
              <Badge tone={statusTone(t.status)}>{t.status}</Badge>
              {t.credential_set ? <Badge tone="green">credential set</Badge> : <Badge tone="rose">no credential</Badge>}
              {t.provision && <Badge tone="slate">provision</Badge>}
              {t.deprovision && <Badge tone="slate">deprovision</Badge>}
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={() => test.mutate(t.id)} data-testid={`outbound-test-${t.id}`}>Test</Button>
                <Button size="sm" variant="outline" onClick={() => del.mutate(t.id)}><Trash /></Button>
              </div>
            </div>
            {t.last_push_at && <p className="text-xs text-slate-400">Last push {t.last_push_at.slice(0, 16).replace('T', ' ')}</p>}
          </Card>
        ))}
      </div>
      {(events || []).length > 0 && (
        <Card className="mt-4">
          <Label>Recent outbound pushes</Label>
          {events.slice(0, 20).map((e) => (
            <div key={e.id} className="py-1.5 border-t border-slate-100 text-sm flex items-center gap-2 flex-wrap">
              <Badge tone={e.status === 'ok' ? 'green' : 'rose'}>{e.status}</Badge>
              <Badge tone="slate">{e.kind}</Badge>
              <span className="text-slate-600">{e.action}</span>
              <span className="text-xs text-slate-400">{e.user_email}</span>
              <span className="text-xs text-slate-400 ml-auto">{e.detail}</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
};

// ---------------- SSO Apps (ONRID as OIDC provider) ----------------
const SSOAppsTab = () => {
  const qc = useQueryClient();
  const { data: clients } = useQuery({ queryKey: ['idp-clients'], queryFn: () => api.get('/idp/clients').then((r) => r.data) });
  const [form, setForm] = useState({ name: '', redirect_uris: '', profile: true, email: true });
  const [secret, setSecret] = useState(null);
  const discovery = `${API}/idp/.well-known/openid-configuration`;
  const create = useMutation({
    mutationFn: () => api.post('/idp/clients', {
      name: form.name,
      redirect_uris: form.redirect_uris.split(/[\n,]/).map((u) => u.trim()).filter(Boolean),
      allowed_scopes: ['openid', ...(form.profile ? ['profile'] : []), ...(form.email ? ['email'] : [])],
    }).then((r) => r.data),
    onSuccess: (d) => { setSecret(d); setForm({ name: '', redirect_uris: '', profile: true, email: true }); qc.invalidateQueries({ queryKey: ['idp-clients'] }); toast.success('App registered'); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const del = useMutation({ mutationFn: (id) => api.delete(`/idp/clients/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ['idp-clients'] }); toast.success('App removed'); }, onError: (e) => toast.error(errMsg(e)) });
  return (
    <div data-testid="dirsync-sso">
      <Card className="mb-4">
        <Label>Discovery URL (give this to your app - it auto-configures endpoints &amp; keys)</Label>
        <div className="bg-slate-50 border border-slate-200 rounded p-2 flex items-center gap-2 mb-3">
          <code className="text-xs flex-1 break-all" data-testid="idp-discovery">{discovery}</code>
          <Button size="sm" variant="outline" onClick={() => copy(discovery)}><Copy /></Button>
        </div>
        <Label>Register a new "Sign in with ONRID" app</Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
          <Input placeholder="App name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sso-name" />
          <textarea className="border border-slate-200 rounded-md p-2 text-xs font-mono h-16" placeholder="Redirect URIs (one per line, https://... or http://localhost...)" value={form.redirect_uris} onChange={(e) => setForm({ ...form, redirect_uris: e.target.value })} data-testid="sso-uris" />
        </div>
        <div className="flex items-center gap-4 mt-2 text-xs text-slate-600">
          <span className="font-mono-label text-[10px] uppercase text-slate-400">Scopes:</span>
          <label className="flex items-center gap-1"><input type="checkbox" checked disabled /> openid</label>
          <label className="flex items-center gap-1"><input type="checkbox" checked={form.profile} onChange={(e) => setForm({ ...form, profile: e.target.checked })} /> profile</label>
          <label className="flex items-center gap-1"><input type="checkbox" checked={form.email} onChange={(e) => setForm({ ...form, email: e.target.checked })} /> email</label>
        </div>
        <Button className="mt-3" size="sm" onClick={() => create.mutate()} disabled={!form.name || !form.redirect_uris || create.isPending} data-testid="sso-create-btn">Register app</Button>
      </Card>
      {secret && (
        <Card className="mb-4 border-amber-300 bg-amber-50" data-testid="sso-secret-card">
          <Label>Copy these now - the secret is shown only once</Label>
          <div className="text-sm space-y-1 mt-1">
            <div className="flex items-center gap-2"><span className="text-slate-400 w-24">Client ID</span><code className="text-xs flex-1 break-all">{secret.client_id}</code><Button size="sm" variant="outline" onClick={() => copy(secret.client_id)}><Copy /></Button></div>
            <div className="flex items-center gap-2"><span className="text-slate-400 w-24">Client secret</span><code className="text-xs flex-1 break-all" data-testid="sso-secret-value">{secret.client_secret}</code><Button size="sm" variant="outline" onClick={() => copy(secret.client_secret)}><Copy /></Button></div>
          </div>
          <Button size="sm" className="mt-2" onClick={() => setSecret(null)}>Done</Button>
        </Card>
      )}
      {(clients || []).length === 0 && <Card><p className="text-sm text-slate-400">No SSO apps yet.</p></Card>}
      <div className="space-y-3">
        {(clients || []).map((c) => (
          <Card key={c.id} data-testid={`sso-card-${c.id}`}>
            <div className="flex items-center gap-3 flex-wrap mb-1">
              <ShieldCheck className="text-blue-600" size={18} />
              <span className="font-semibold">{c.name}</span>
              <Badge tone={c.active ? 'green' : 'slate'}>{c.active ? 'active' : 'disabled'}</Badge>
              <code className="text-[10px] text-slate-400">{c.client_id}</code>
              <Button size="sm" variant="outline" className="ml-auto" onClick={() => del.mutate(c.id)} data-testid={`sso-del-${c.id}`}><Trash /></Button>
            </div>
            <div className="text-xs text-slate-500">
              <span className="font-mono-label text-[10px] uppercase text-slate-400 mr-2">Scopes</span>{c.allowed_scopes.join(', ')}
              <div className="mt-1"><span className="font-mono-label text-[10px] uppercase text-slate-400 mr-2">Redirects</span>{c.redirect_uris.join('  ·  ')}</div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ---------------- Documentation ----------------
const DOCS = [
  ['Overview', ['Zero-trust, mapping-driven provisioning from AD, Entra ID and Okta into ONRID.', 'Outbound: push create/disable back to Entra ID and Google Workspace.', 'Every user is provisioned only if a mapping matches (default deny). Secrets encrypted at rest.']],
  ['1. Windows AD Agent', ['AD Agents > New agent: add forests, domains and Domain Controller hosts (LDAPS 636), plus a read-only bind account per domain.', 'Download the ZIP (Python + PowerShell agent + installer + README).', 'Run on a Windows host near your DCs; first run swaps the enrollment token for a rotating API key.', 'Install as a scheduled task (PowerShell) or Windows service via NSSM (Python).']],
  ['2. Entra ID', ['App Registration with Graph app permissions User.Read.All + Group.Read.All (admin-consented).', 'Paste tenant ID, client ID, client secret in the Entra ID tab. Test, then Sync now.']],
  ['3. Okta', ['Create a read-only API token in Okta Admin > Security > API > Tokens.', 'Paste org URL + token in the Okta tab. Test, then Sync now.']],
  ['4. Mappings', ['Default deny: add rules like group contains "Domain Teachers" -> teacher, or department equals "Finance" -> finance_admin.', 'Evaluated top-down by priority; first match wins. Never assigns global_admin.']],
  ['5. Outbound provisioning', ['Add an Entra target (Graph User.ReadWrite.All) or Google Workspace target (service account with domain-wide delegation).', 'ONRID auto-pushes account create/disable to every enabled target; each push is logged ok/error.']],
  ['6. Risk & Insights', ['Transparent dropout-risk scoring from attendance, marks and arrears - every point explained.', 'Open a student for an AI summary + 3 interventions (respects AI consent).', 'A nightly job alerts advisors and emails guardians when a student crosses into high/critical risk.']],
  ['7. Sign in with ONRID (SSO)', ['Register your other school apps under SSO Apps to let them "Sign in with ONRID" (OpenID Connect, authorization-code + PKCE).', 'Give the app the Discovery URL; it auto-configures endpoints and signing keys. Copy the client secret once at registration.', 'Cloud connectors sync automatically every night when "Nightly auto-sync" is on (toggle on each Entra/Okta connector).']],
];

const DocsTab = () => (
  <div data-testid="dirsync-docs">
    <div className="flex items-center justify-between mb-3">
      <p className="text-sm text-slate-500">End-to-end setup guide. Download the branded PDF to share with your IT team.</p>
      <Button size="sm" onClick={() => window.open(`${API}/dirsync/docs.pdf`, '_blank')} data-testid="docs-download-pdf"><DownloadSimple className="mr-1" /> Download PDF guide</Button>
    </div>
    {DOCS.map(([title, points]) => (
      <Card key={title} className="mb-3">
        <p className="font-display font-bold mb-2">{title}</p>
        <ul className="list-disc pl-5 space-y-1 text-sm text-slate-600">
          {points.map((p, i) => <li key={i}>{p}</li>)}
        </ul>
      </Card>
    ))}
  </div>
);

// ---------------- Logs ----------------
const LogsTab = () => {
  const { data: runs } = useQuery({ queryKey: ['dirsync-runs'], queryFn: () => api.get('/dirsync/runs').then((r) => r.data) });
  return (
    <div data-testid="dirsync-logs">
      {(runs || []).length === 0 && <Card><p className="text-sm text-slate-400">No sync activity yet.</p></Card>}
      <div className="space-y-2">
        {(runs || []).map((r) => (
          <Card key={r.id}>
            <div className="flex items-center gap-3 flex-wrap text-sm mb-1">
              <Badge tone="slate">{r.source}</Badge>
              <span className="text-xs text-slate-400">{(r.finished_at || '').slice(0, 16).replace('T', ' ')}</span>
              <span className="text-green-700">+{r.counts?.created || 0}</span>
              <span className="text-blue-700">{r.counts?.updated || 0} upd</span>
              <span className="text-amber-700">{r.counts?.deactivated || 0} disabled</span>
              <span className="text-rose-700">{r.counts?.rejected || 0} rejected</span>
              <span className="text-slate-400">{r.counts?.skipped || 0} skipped · {r.counts?.errors || 0} errors</span>
            </div>
            {r.detail && <p className="text-xs text-slate-400">{r.detail}</p>}
            {(r.samples || []).length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {r.samples.slice(0, 12).map((s, i) => (
                  <span key={i} className="text-[10px] text-slate-500 bg-slate-50 border border-slate-100 rounded px-1.5 py-0.5">{s.email}: {s.action}</span>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default function DirectorySync() {
  const { data } = useQuery({ queryKey: ['dirsync-overview'], queryFn: () => api.get('/dirsync/overview').then((r) => r.data), refetchInterval: 15000 });
  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto" data-testid="directory-sync-page">
      <div className="flex items-center gap-3 mb-1">
        <TreeStructure className="text-blue-600" size={26} />
        <h1 className="font-display text-2xl font-extrabold">Directory Sync</h1>
      </div>
      <p className="text-sm text-slate-500 mb-4">Import identities from on-prem Active Directory (multi-forest, multi-DC), Microsoft Entra ID and Okta. Inbound, zero-trust, mapping-driven provisioning into ONRID.</p>
      <Tabs defaultValue="overview">
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="agents" data-testid="tab-agents">AD Agents</TabsTrigger>
          <TabsTrigger value="entra" data-testid="tab-entra">Entra ID</TabsTrigger>
          <TabsTrigger value="okta" data-testid="tab-okta">Okta</TabsTrigger>
          <TabsTrigger value="mappings" data-testid="tab-mappings">Mappings</TabsTrigger>
          <TabsTrigger value="outbound" data-testid="tab-outbound">Outbound</TabsTrigger>
          <TabsTrigger value="sso" data-testid="tab-sso">SSO Apps</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs">Docs</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Sync Logs</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><OverviewTab data={data} /></TabsContent>
        <TabsContent value="agents"><AgentsTab agents={data?.agents} /></TabsContent>
        <TabsContent value="entra"><ConnectorsTab kind="entra" /></TabsContent>
        <TabsContent value="okta"><ConnectorsTab kind="okta" /></TabsContent>
        <TabsContent value="mappings"><MappingsTab /></TabsContent>
        <TabsContent value="outbound"><OutboundTab /></TabsContent>
        <TabsContent value="sso"><SSOAppsTab /></TabsContent>
        <TabsContent value="docs"><DocsTab /></TabsContent>
        <TabsContent value="logs"><LogsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
